import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CenteredLoader } from '../../components/LoadingSpinner';
import { api } from '../../shared/api';
import { useRealtimeEvents } from '../../shared/useRealtimeEvents';
import { useCustomer } from '../../entities/customer';
import { getContrastColor, COLOR_PRIMARY, COLOR_SUCCESS, COLOR_WARNING, COLOR_DANGER, COLOR_SECONDARY, COLOR_DARK, COLOR_GREEN, COLOR_BLUE } from '../../shared/colorUtils';

const CustomerDetail = () => {
  const { customerId } = useParams();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState('overview');

  const { data: customer, isLoading: loading, isError } = useCustomer(customerId);
  const statistics = customer?.statistics ?? null;
  const currentRentals = customer?.current_rentals ?? [];
  const error = isError ? 'Failed to load customer details. Please try again later.' : null;

  const { data: allRentals = [], refetch: refetchAllRentals } = useQuery({
    queryKey: ['customer-all-rentals', customerId],
    queryFn: async () => {
      const response = await api.get(`customers/api/customers/${customerId}/rentals/`);
      return response.rentals || [];
    },
    enabled: activeTab === 'rentals',
  });

  useRealtimeEvents((e) => {
    if (e.model === 'rentals.rental' || e.model === 'rentals.rentalitem') {
      queryClient.invalidateQueries({ queryKey: ['customer-all-rentals', customerId] });
    }
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('cs-CZ', {
      style: 'currency',
      currency: 'CZK'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('cs-CZ');
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'ongoing': return COLOR_PRIMARY;
      case 'pending_return': return COLOR_WARNING;
      case 'completed': return COLOR_SUCCESS;
      case 'cancelled': return COLOR_DANGER;
      case 'canceled': return COLOR_DANGER;
      case 'expired': return COLOR_DARK;
      default: return COLOR_SECONDARY;
    }
  };

  const getStatusText = (status) => {
    return t(`rentals.statuses.${status}`, { defaultValue: status });
  };

  if (loading) {
    return (
      <div className="container-xl">
        <CenteredLoader message={t('customers.loading')} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-xl">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="container-xl">
        <div className="alert alert-warning" role="alert">
          {t('customers.notFound')}
        </div>
      </div>
    );
  }

  return (
    <div className="container-xl">
      {/* Page Header */}
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <div className="page-pretitle">
                {t('customers.title')}
              </div>
              <h2 className="page-title">
                {customer.company_name}
              </h2>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <div className="btn-list">
                <Link
                  to="/customers"
                  className="btn btn-outline-secondary"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-arrow-left" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <path d="M5 12l14 0"></path>
                    <path d="M5 12l6 6"></path>
                    <path d="M5 12l6 -6"></path>
                  </svg>
                  {t('common.back')}
                </Link>
                <Link
                  to={`/customers/customer/${customerId}/edit`}
                  className="btn btn-primary"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                    <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" />
                    <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z" />
                    <path d="M16 5l3 3" />
                  </svg>
                  {t('common.edit')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Page Body */}
      <div className="page-body">
        <div className="container-xl">
          {/* Customer Overview Cards */}
          <div className="row row-deck row-cards mb-4">
            <div className="col-sm-6 col-lg-3">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="subheader">{t('customers.totalRentals')}</div>
                    <div className="ms-auto lh-1">
                      <div className="dropdown">
                        <a className="dropdown-toggle text-muted" href="#" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false">{t('customers.last30Days')}</a>
                        <div className="dropdown-menu dropdown-menu-end">
                          <a className="dropdown-item active" href="#">{t('customers.last30Days')}</a>
                          <a className="dropdown-item" href="#">{t('customers.last90Days')}</a>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="h1 mb-3">{statistics?.total_rentals || 0}</div>
                  <div className="d-flex mb-2">
                    <div>{t('customers.activeLabel')}: {statistics?.active_rentals || 0}</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-sm-6 col-lg-3">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="subheader">{t('customers.totalRevenue')}</div>
                  </div>
                  <div className="h1 mb-3">{formatCurrency(statistics?.total_revenue)}</div>
                  <div className="d-flex mb-2">
                    <div>{t('customers.avgLabel')}: {formatCurrency(statistics?.avg_rental_value)}</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-sm-6 col-lg-3">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="subheader">{t('customers.discount')}</div>
                  </div>
                  <div className="h1 mb-3">{customer.discount}%</div>
                  <div className="d-flex mb-2">
                    <div className="text-muted">{t('customers.customerDiscountRate')}</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-sm-6 col-lg-3">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="subheader">{t('customers.recentActivity')}</div>
                  </div>
                  <div className="h1 mb-3">{statistics?.rentals_last_30_days || 0}</div>
                  <div className="d-flex mb-2">
                    <div>{t('customers.last30Days')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="card">
            <div className="card-header">
              <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs">
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setActiveTab('overview')}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="icon me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                      <path d="M5 5a5 5 0 0 1 7 0a5 5 0 0 0 7 0v9a5 5 0 0 1 -7 0a5 5 0 0 0 -7 0v-9z"/>
                      <path d="M5 21v-7"/>
                    </svg>
                    {t('customers.overview')}
                  </button>
                </li>
                  <li className="nav-item">
                      <button
                          className={`nav-link ${activeTab === 'rentals' ? 'active' : ''}`}
                          onClick={() => {
                              setActiveTab('rentals');
                          }}
                      >
                    <svg xmlns="http://www.w3.org/2000/svg" className="icon me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                      <path d="M9.615 20h-2.615a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h8a2 2 0 0 1 2 2v8"></path>
                      <path d="M14 19l2 2l4 -4"></path>
                      <path d="M9 8h4"></path>
                      <path d="M9 12h2"></path>
                    </svg>
                    {t('rentals.title')}
                  </button>
                </li>
                <li className="nav-item">
                  <button
                      className={`nav-link ${activeTab === 'statistics' ? 'active' : ''}`}
                      onClick={() => setActiveTab('statistics')}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="icon me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                      <path d="M4 19l4 -6l4 2l4 -5l4 4l0 5l-16 0"></path>
                      <path d="M4 12l3 -4l4 2l5 -6l4 4"></path>
                    </svg>
                    {t('customers.statistics')}
                  </button>
                </li>
              </ul>
            </div>

              <div className="card-body">
                  {/* Overview Tab */}
                  {activeTab === 'overview' && (
                      <div className="row">
                          <div className="col-md-6">
                              <div className="card">
                                  <div className="card-header">
                                      <h3 className="card-title">{t('customers.contactInformation')}</h3>
                      </div>
                      <div className="card-body">
                        <div className="datagrid">
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('customers.form.companyName')}</div>
                            <div className="datagrid-content">{customer.company_name}</div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('customers.form.companyId')}</div>
                            <div className="datagrid-content">{customer.company_id || '-'}</div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('customers.form.respPerson')}</div>
                            <div className="datagrid-content">{customer.resp_person || '-'}</div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('customers.form.email')}</div>
                            <div className="datagrid-content">
                              {customer.email ? (
                                <a href={`mailto:${customer.email}`}>{customer.email}</a>
                              ) : '-'}
                            </div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('customers.form.telephone')}</div>
                            <div className="datagrid-content">
                              {customer.telephone ? (
                                <a href={`tel:${customer.telephone}`}>{customer.telephone}</a>
                              ) : '-'}
                            </div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('customers.form.mobile')}</div>
                            <div className="datagrid-content">
                              {customer.mobile ? (
                                <a href={`tel:${customer.mobile}`}>{customer.mobile}</a>
                              ) : '-'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-header">
                        <h3 className="card-title">{t('customers.addressInformation')}</h3>
                      </div>
                      <div className="card-body">
                        <div className="datagrid">
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('customers.form.street')}</div>
                            <div className="datagrid-content">{customer.street || '-'}</div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('customers.form.city')}</div>
                            <div className="datagrid-content">{customer.city || '-'}</div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('customers.form.postalCode')}</div>
                            <div className="datagrid-content">{customer.postal_code || '-'}</div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('customers.form.taxId')}</div>
                            <div className="datagrid-content">{customer.tax_id || '-'}</div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('customers.form.discount')}</div>
                            <div className="datagrid-content">
                              <span className="badge" style={{ backgroundColor: COLOR_GREEN, color: getContrastColor(COLOR_GREEN) }}>{customer.discount}%</span>
                            </div>
                          </div>
                          {customer.description && (
                            <div className="datagrid-item">
                              <div className="datagrid-title">{t('customers.form.description')}</div>
                              <div className="datagrid-content">{customer.description}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Current Rentals */}
                    {currentRentals.length > 0 && (
                      <div className="card mt-3">
                        <div className="card-header">
                          <h3 className="card-title">{t('customers.currentRentals')}</h3>
                          <div className="card-actions">
                            <span className="badge" style={{ backgroundColor: COLOR_PRIMARY, color: getContrastColor(COLOR_PRIMARY) }}>{currentRentals.length}</span>
                          </div>
                        </div>
                        <div className="card-body p-0">
                          <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            <table className="table table-vcenter">
                              <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10 }}>
                                <tr>
                                  <th>{t('rentals.rentalDate')}</th>
                                  <th>{t('rentals.status')}</th>
                                  <th>{t('rentals.price')}</th>
                                  <th>{t('rentals.items')}</th>
                                  <th className="w-1">{t('common.actions')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {currentRentals.map(rental => (
                                  <tr key={rental.id}>
                                    <td>{formatDate(rental.rental_date)}</td>
                                    <td>
                                      <span className="badge" style={{ backgroundColor: getStatusBadgeColor(rental.rental_status), color: getContrastColor(getStatusBadgeColor(rental.rental_status)) }}>
                                        {getStatusText(rental.rental_status)}
                                      </span>
                                    </td>
                                    <td>{formatCurrency(rental.price)}</td>
                                    <td>
                                        <div className="text-truncate" style={{ maxWidth: '100%' }}>
                                        {rental.rental_items?.map(item => item.stock_item_product_name || '-').join(', ')}
                                      </div>
                                    </td>
                                    <td>
                                      <Link
                                        to={`/rentals/rental/${rental.id}`}
                                        className="btn btn-sm btn-outline-primary"
                                      >
                                        {t('common.view')}
                                      </Link>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Rentals Tab */}
              {activeTab === 'rentals' && (
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">{t('customers.rentalHistory')}</h3>
                    <div className="card-actions">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={refetchAllRentals}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                          <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4"/>
                          <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4"/>
                        </svg>
                        {t('common.refresh')}
                      </button>
                    </div>
                  </div>
                  <div className="card-body p-0">
                    <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                      <table className="table table-vcenter">
                        <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10 }}>
                          <tr>
                            <th>{t('rentals.rentalDate')}</th>
                            <th>{t('rentals.dateFrom')}</th>
                            <th>{t('rentals.dateTo')}</th>
                            <th>{t('rentals.status')}</th>
                            <th>{t('rentals.price')}</th>
                            <th>{t('rentals.items')}</th>
                            <th className="w-1">{t('common.actions')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allRentals.length === 0 ? (
                            <tr>
                              <td colSpan="7" className="text-center text-muted py-4">
                                {t('customers.noRentals')}
                              </td>
                            </tr>
                          ) : (
                            allRentals.map(rental => (
                              <tr key={rental.id}>
                                <td>{formatDate(rental.rental_date)}</td>
                                <td>{formatDate(rental.date_from)}</td>
                                <td>{formatDate(rental.date_to)}</td>
                                <td>
                                  <span className="badge" style={{ backgroundColor: getStatusBadgeColor(rental.rental_status), color: getContrastColor(getStatusBadgeColor(rental.rental_status)) }}>
                                    {getStatusText(rental.rental_status)}
                                  </span>
                                </td>
                                <td>{formatCurrency(rental.price)}</td>
                                <td>
                                    <div className="text-truncate" style={{ maxWidth: '100%' }}>
                                    {rental.rental_items?.map(item =>
                                      `${item.stock_item_product_name || '-'} (${item.quantity})`
                                    ).join(', ')}
                                  </div>
                                </td>
                                <td>
                                  <div className="btn-group">
                                    <Link
                                      to={`/rentals/rental/${rental.id}`}
                                      className="btn btn-sm btn-outline-primary"
                                      title={t('common.view')}
                                    >
                                      <i className="ti ti-eye"></i>
                                    </Link>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Statistics Tab */}
              {activeTab === 'statistics' && (
                <div className="row">
                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-header">
                        <h3 className="card-title">{t('customers.rentalStatistics')}</h3>
                      </div>
                      <div className="card-body">
                        <div className="datagrid">
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('customers.totalRentals')}</div>
                            <div className="datagrid-content">
                              <strong>{statistics?.total_rentals || 0}</strong>
                            </div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('customers.activeRentals')}</div>
                            <div className="datagrid-content">
                              <span className="badge" style={{ backgroundColor: COLOR_PRIMARY, color: getContrastColor(COLOR_PRIMARY) }}>{statistics?.active_rentals || 0}</span>
                            </div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('customers.completedRentals')}</div>
                            <div className="datagrid-content">
                              <span className="badge" style={{ backgroundColor: COLOR_SUCCESS, color: getContrastColor(COLOR_SUCCESS) }}>{statistics?.completed_rentals || 0}</span>
                            </div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('customers.firstRental')}</div>
                            <div className="datagrid-content">{formatDate(statistics?.first_rental_date)}</div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('customers.rentalsLast30Days')}</div>
                            <div className="datagrid-content">{statistics?.rentals_last_30_days || 0}</div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('customers.rentalsLast90Days')}</div>
                            <div className="datagrid-content">{statistics?.rentals_last_90_days || 0}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-header">
                        <h3 className="card-title">{t('customers.financialStatistics')}</h3>
                      </div>
                      <div className="card-body">
                        <div className="datagrid">
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('customers.totalRevenue')}</div>
                            <div className="datagrid-content">
                              <strong>{formatCurrency(statistics?.total_revenue)}</strong>
                            </div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('customers.avgRentalValue')}</div>
                            <div className="datagrid-content">{formatCurrency(statistics?.avg_rental_value)}</div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('customers.form.discount')}</div>
                            <div className="datagrid-content">
                              <span className="badge" style={{ backgroundColor: COLOR_GREEN, color: getContrastColor(COLOR_GREEN) }}>{customer.discount}%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Most Rented Items */}
                    {statistics?.most_rented_items && statistics.most_rented_items.length > 0 && (
                      <div className="card mt-3">
                        <div className="card-header">
                          <h3 className="card-title">{t('customers.mostRentedItems')}</h3>
                        </div>
                        <div className="card-body p-0">
                          <div className="table-responsive">
                            <table className="table table-vcenter">
                              <thead>
                                <tr>
                                  <th>{t('warehouses.items.name')}</th>
                                  <th>{t('customers.totalQuantity')}</th>
                                  <th>{t('customers.rentalCount')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {statistics.most_rented_items.map((item, index) => (
                                  <tr key={index}>
                                    <td>
                                      <Link to={`/warehouses/items/${item.item__id}`}>
                                        {item.item__name}
                                      </Link>
                                    </td>
                                    <td>
                                      <span className="badge" style={{ backgroundColor: COLOR_BLUE, color: getContrastColor(COLOR_BLUE) }}>{item.total_quantity}</span>
                                    </td>
                                    <td>{item.rental_count}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetail;
